// Global Instructions Rule Applied!
//
// Investor form HTTP calls (backend module `investors`). Three reads, each
// validated against its Zod model before it reaches the form, and one write:
//   GET   /investors/:id              → view__investor_detail
//   GET   /investors/:id/commitments  → view__investor_commitments
//   GET   /investors/form-options     → view__investor_form_options
//   POST  /investors (create) · PATCH /investors (edit) → investors__save
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  type InvestorCommitmentRow,
  type InvestorDetail,
  type InvestorFormOptions,
  validateInvestorCommitmentRows,
  validateInvestorDetail,
  validateInvestorFormOptions
} from '../../../state_manager/data_models/investor.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const authHeaders = (accessToken: string | null): Record<string, string> => ({
  'Content-Type': 'application/json',
  ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
})

type ErrorBody = { data?: unknown; message?: string; error?: string }

const readError = (body: ErrorBody, status: number, fallback: string): string => {
  if (status === 401) return 'Authentication failed - please log in again'
  return body.message || body.error || `${fallback} (${status})`
}

/** PARTIAL by design: investors__save writes only the keys present. Create
 *  sends account_id + name and no id; edit sends id and only the fields being
 *  changed (a key with null clears that field, an absent key keeps it). */
export interface InvestorSavePayload {
  id?: string
  /** create only — the account the investor belongs to */
  account_id?: string
  name?: string
  legal_name?: string | null
  investor_number?: string | null
  investor_classification_id?: number | null
  legal_entity_type_id?: number | null
  sbic_investor_type_id?: number | null
  /** cfg.notice_templates supplements; the investor's live set becomes exactly this. */
  notice_supplement_template_ids?: number[]
}

export const fetchInvestorDetail = async (
  investorId: string,
  accessToken: string | null = null
): Promise<{ data: InvestorDetail | null; notFound?: boolean; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTORS.GET, { id: investorId }, {}), {
      method: API_ROUTES.INVESTORS.GET.method,
      headers: authHeaders(accessToken)
    })
    const body = (await response.json().catch(() => ({}))) as ErrorBody
    if (response.status === 404) return { data: null, notFound: true }
    if (!response.ok) throw new Error(readError(body, response.status, 'Failed to fetch the investor'))
    return { data: validateInvestorDetail(body.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching investor detail:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchInvestorCommitments = async (
  investorId: string,
  accessToken: string | null = null
): Promise<{ data: InvestorCommitmentRow[]; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTORS.COMMITMENTS, { id: investorId }, {}),
      { method: API_ROUTES.INVESTORS.COMMITMENTS.method, headers: authHeaders(accessToken) }
    )
    const body = (await response.json().catch(() => ({}))) as ErrorBody
    if (!response.ok) throw new Error(readError(body, response.status, "Failed to fetch the investor's commitments"))
    return { data: validateInvestorCommitmentRows(body.data ?? []) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching investor commitments:', error)
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchInvestorFormOptions = async (
  accessToken: string | null = null
): Promise<{ data: InvestorFormOptions | null; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INVESTORS.FORM_OPTIONS, {}, {}), {
      method: API_ROUTES.INVESTORS.FORM_OPTIONS.method,
      headers: authHeaders(accessToken)
    })
    const body = (await response.json().catch(() => ({}))) as ErrorBody
    if (!response.ok) throw new Error(readError(body, response.status, 'Failed to fetch the investor form options'))
    return { data: validateInvestorFormOptions(body.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching investor form options:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** Create (no id) or edit (id) an investor entity. Answers the fresh detail. */
export const saveInvestor = async (
  payload: InvestorSavePayload,
  accessToken: string | null = null
): Promise<{ data: InvestorDetail | null; error?: string }> => {
  try {
    const route = payload.id ? API_ROUTES.INVESTORS.PATCH : API_ROUTES.INVESTORS.CREATE
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, route, {}, {}), {
      method: route.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })
    const body = (await response.json().catch(() => ({}))) as ErrorBody
    if (!response.ok) throw new Error(readError(body, response.status, 'Failed to save the investor'))
    return { data: validateInvestorDetail(body.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error saving investor:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}
